import OrderModal from "@/components/core/modals/OrderModal";
import { requestGetBalanceForOffice } from "@/state/balance/BalanceEffects";
import { requestGetBills } from "@/state/bill/BillEffects";
import { requsetGetConsumptionStats } from "@/state/consumptionStat/StatEffects";
import SherlockHelper from "@/utils/helpers/sherlock-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import PageLayout from "@components/core/layouts/PageLayout";
import CardSection from "@components/core/section/CardSection";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ROUTES } from "@routes/Routes";
import { Bill, Facturation, Stat } from "@type/core/offices/offices.types";
import { LIMIT_CREDIT_WARNING_VALUE } from "@utils/Constant";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BalanceAndStatisticsTabContent from "@views/core/extranet/menu/offices/balanceAndStatistics/BalanceAndStatisticsTabContent";
import { useNavigate, useParams } from "react-router-dom";

const BalanceAndStatisticsScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const { officeId } = useParams<{ officeId?: string }>();
  const navigate = useNavigate();

  const CURRENT_YEAR = new Date().getFullYear();

  const { selectedOffice, officesToDisplay, handleTabClick, activeKey } =
    useOfficesToDisplay(officeId);

  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [facturation, setFacturation] = useState<Facturation | undefined>();
  const [bills, setBills] = useState<Bill[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [isOrderModalVisible, setIsOrderModalVisible] =
    useState<boolean>(false);
  const [isOrderModalLoading, setIsOrderModalLoading] = useState(false);

  useEffect(() => {
    if (!Number(officeId)) {
      navigate(
        ROUTES.extranet.menu.offices.balanceAndStatistics.generate(
          officesToDisplay[0].id,
        ),
      );
    }
  });

  useEffect(() => {
    void requestGetBalanceForOffice({
      dto: {
        officeIds: officesToDisplay.map((office) => office.id).join(","),
      },
    });
  }, [selectedOffice]);

  useEffect(() => {
    void requestGetBills({});
  }, []);

  useEffect(() => {
    void requsetGetConsumptionStats({
      dto: {
        year: `${selectedYear}`,
      },
    });
  }, [selectedYear]);

  useEffect(() => {
    requestGetBalanceForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        if (selectedOffice) {
          setFacturation(result.data);
        }
      } else {
        toastError(
          `${result.responseCode} : ${t("error.toast.officesBalance")}`,
        );
      }
    });
  }, [selectedOffice]);

  useEffect(() => {
    void requestGetBills.done.watch(({ result }) => {
      if (result && result.ok) {
        setBills(result.data?.bills ?? []);
      } else {
        toastError(`${result.responseCode} : ${t("error.toast.bills")}`);
      }
    });
  }, []);

  useEffect(() => {
    void requsetGetConsumptionStats.done.watch(({ result }) => {
      if (result && result.ok) {
        setStats(result.data ?? []);
      } else {
        toastError(`${result.responseCode} : ${t("error.toast.bills")}`);
      }
    });
  }, []);

  const isWarning = (officeId: string, officeInGroup?: boolean) => {
    if (!facturation || officeInGroup) {
      return false;
    }
    const balance = facturation.officesBalance.find(
      (thisOffice) => thisOffice.officeId === officeId,
    );
    return (
      balance?.balanceInCents !== undefined &&
      balance.balanceInCents < LIMIT_CREDIT_WARNING_VALUE
    );
  };

  const isAnyWarning = () => {
    if (!facturation) {
      return false;
    }
    return facturation.officesBalance.some(
      (balance) =>
        balance.balanceInCents &&
        balance.balanceInCents < LIMIT_CREDIT_WARNING_VALUE,
    );
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection
          title={t("offices.balanceAndStatistics.title")}
          subtitle={
            isAnyWarning() ? (
              <>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="mr-4"
                />
                {t("offices.balanceAndStatistics.refill")}
              </>
            ) : (
              <></>
            )
          }
          subtitleClassName="text-center text-danger"
        >
          <BasicTabs
            activeKey={activeKey}
            onTabClick={handleTabClick}
            tabs={officesToDisplay.map((office) => {
              return {
                title: (
                  <>
                    {isWarning(office.id, office.belongsToGroup) && (
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="text-danger mr-4"
                      />
                    )}
                    {office.alias}
                  </>
                ),
                content: (
                  <BalanceAndStatisticsTabContent
                    key={`BalanceAndStatistics_BalanceAndStatisticsTabContent_${office.id}`}
                    office={office}
                    facturation={facturation}
                    bills={bills}
                    stats={stats}
                    setIsOrderModalVisible={setIsOrderModalVisible}
                    selectedYear={selectedYear}
                    setSelectedYear={setSelectedYear}
                  />
                ),
              };
            })}
          />
        </CardSection>
        {selectedOffice?.billing && (
          <OrderModal
            title={t("modals.orderModal.title")}
            visible={isOrderModalVisible}
            onCancel={() => setIsOrderModalVisible(false)}
            officeBalance={facturation?.officesBalance.find(
              (facture) => facture.officeId === selectedOffice?.id,
            )}
            billing={selectedOffice.billing}
            isLoading={isOrderModalLoading}
            onSubmit={(tarif) => {
              setIsOrderModalLoading(true);
              location.href = SherlockHelper.buildOrderUrl(
                selectedOffice.id,
                tarif,
              );
            }}
          />
        )}
      </>
    </PageLayout>
  );
};

export default BalanceAndStatisticsScreen;
